import { apiClient as api } from "./httpClient";

// Chat interno do tenant (RN-551): conversas diretas entre pessoas da mesma entidade.
// Datas chegam no horário da entidade, sem fuso (padrão da API) — exibir como vieram.

export interface ChatContact {
  userId: string;
  name: string;
  role: string | null;
  hasPhoto: boolean;
  threadId: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
  lastMessageMine: boolean;
  lastMessageRead: boolean;
  unread: number;
  online: boolean;
  /** Sininho - Recepção Digital (RN-551f): só envia avisos; a conversa não tem campo de resposta */
  isSystem?: boolean;
}

export interface ChatAttachment {
  name: string;
  size: number;
  contentType: string;
  isImage: boolean;
  hasThumb: boolean;
}

/** Ação de um aviso do Sininho (RN-551f): "open-patient" abre a ficha do paciente. */
export interface ChatMessageAction {
  type: string;
  patientId: string | null;
  scheduleId: string | null;
}

export interface ChatMessage {
  id: string;
  mine: boolean;
  content: string;
  sentAt: string;
  attachment?: ChatAttachment | null;
  action?: ChatMessageAction | null;
  /** Só em grupo (RN-551h): quem mandou, para o balão dizer o nome */
  senderId?: string | null;
  senderName?: string | null;
  /** Ids dos membros citados com @ (grupo); nulo sem menção */
  mentions?: string[] | null;
}

/** Grupo do chat (RN-551h): conversa com título e N pessoas da unidade */
export interface ChatGroup {
  threadId: string;
  title: string;
  memberCount: number;
  /** "admin" (quem criou) ou "member" */
  myRole: string;
  lastMessage: string | null;
  lastMessageByName: string | null;
  lastMessageAt: string | null;
  lastMessageMine: boolean;
  unread: number;
  /** Entre os não-lidos, quantos me mencionam com @ */
  mentionedUnread?: number;
}

export interface ChatGroupMember {
  userId: string;
  name: string;
  role: string | null;
  memberRole: string;
  hasPhoto: boolean;
  online: boolean;
  isMe: boolean;
}

export interface ChatGroupDetail {
  group: ChatGroup;
  members: ChatGroupMember[];
}

export interface ChatThreadMessages {
  threadId: string | null;
  messages: ChatMessage[];
  otherLastReadAt: string | null;
  /** Só no polling com `after`: mensagens apagadas desde então, para tirar da tela */
  deletedIds?: string[] | null;
}

export interface ChatUnreadThread {
  threadId: string;
  /** Vazio quando é grupo */
  otherUserId: string;
  unread: number;
  lastMessageAt: string | null;
  /** "direct" ou "group" */
  kind?: string;
}

export interface ChatUnread {
  total: number;
  threads: ChatUnreadThread[];
  /** Ids dos contatos vistos nos últimos 20 s (presença pelo próprio polling) */
  online: string[];
}

// Evento global: o ChatLauncher publica o total de não-lidos e o botão do topo escuta.
export const CHAT_UNREAD_EVENT = "clinic4us:chat-unread";
// Evento global: o botão do topo (ou qualquer tela) pede para abrir o chat.
export const CHAT_OPEN_EVENT = "clinic4us:open-chat";
// Último total publicado: quem monta depois do evento (botão do topo) lê daqui.
export const chatUnreadStore = { total: 0 };

export const chatService = {
  contacts: async (): Promise<ChatContact[]> => {
    const response = await api.get("/chat/contacts");
    return response.data;
  },

  unread: async (): Promise<ChatUnread> => {
    const response = await api.get("/chat/unread");
    return response.data;
  },

  messages: async (otherUserId: string, after?: string | null): Promise<ChatThreadMessages> => {
    const qs = after ? `?after=${encodeURIComponent(after)}` : "";
    const response = await api.get(`/chat/messages/${otherUserId}${qs}`);
    return response.data;
  },

  send: async (otherUserId: string, content: string): Promise<ChatMessage> => {
    const response = await api.post("/chat/messages", { otherUserId, content });
    return response.data;
  },

  // Exclusão lógica da própria mensagem: some das telas, fica na base até o expurgo
  deleteMessage: async (messageId: string): Promise<void> => {
    await api.delete(`/chat/messages/${messageId}`);
  },

  markRead: async (threadId: string): Promise<void> => {
    await api.put(`/chat/threads/${threadId}/read`);
  },

  // Anexo (imagem, PDF ou documento até 10 MB) com legenda opcional. FormData exige Content-Type nulo.
  sendAttachment: async (otherUserId: string, file: File, caption?: string): Promise<ChatMessage> => {
    const form = new FormData();
    form.append("file", file, file.name);
    if (caption) form.append("caption", caption);
    const response = await api.post(`/chat/messages/${otherUserId}/attachment`, form, { headers: { "Content-Type": null }, timeout: 120000 });
    return response.data;
  },

  // Conteúdo do anexo como object URL (proxy autenticado); quem chama revoga.
  attachment: async (messageId: string, thumb: boolean): Promise<Blob> => {
    const response = await api.get(`/chat/attachments/${messageId}${thumb ? "?size=thumb" : ""}`, { responseType: "blob", timeout: 120000 });
    return response.data;
  },

  // Foto do contato (proxy autenticado) como object URL; quem chama revoga.
  photo: async (userId: string): Promise<string> => {
    const response = await api.get(`/chat/contacts/${userId}/photo?size=thumb`, { responseType: "blob" });
    return URL.createObjectURL(response.data);
  },

  // ---------- Grupos (RN-551h): rotas por threadId ----------
  groups: async (): Promise<ChatGroup[]> => {
    const response = await api.get("/chat/groups");
    return response.data;
  },

  createGroup: async (title: string, memberUserIds: string[]): Promise<ChatGroup> => {
    const response = await api.post("/chat/groups", { title, memberUserIds });
    return response.data;
  },

  group: async (threadId: string): Promise<ChatGroupDetail> => {
    const response = await api.get(`/chat/groups/${threadId}`);
    return response.data;
  },

  groupMessages: async (threadId: string, after?: string | null): Promise<ChatThreadMessages> => {
    const qs = after ? `?after=${encodeURIComponent(after)}` : "";
    const response = await api.get(`/chat/groups/${threadId}/messages${qs}`);
    return response.data;
  },

  sendToGroup: async (threadId: string, content: string): Promise<ChatMessage> => {
    const response = await api.post(`/chat/groups/${threadId}/messages`, { content });
    return response.data;
  },

  sendGroupAttachment: async (threadId: string, file: File, caption?: string): Promise<ChatMessage> => {
    const form = new FormData();
    form.append("file", file, file.name);
    if (caption) form.append("caption", caption);
    const response = await api.post(`/chat/groups/${threadId}/attachment`, form, { headers: { "Content-Type": null }, timeout: 120000 });
    return response.data;
  },

  leaveGroup: async (threadId: string): Promise<void> => {
    await api.post(`/chat/groups/${threadId}/leave`);
  },

  // ---------- Grupos, fase 2: administração (só administrador do grupo) e "lido por" ----------
  renameGroup: async (threadId: string, title: string): Promise<ChatGroup> => {
    const response = await api.put(`/chat/groups/${threadId}`, { title });
    return response.data;
  },

  addGroupMembers: async (threadId: string, userIds: string[]): Promise<ChatGroupDetail> => {
    const response = await api.post(`/chat/groups/${threadId}/members`, { userIds });
    return response.data;
  },

  removeGroupMember: async (threadId: string, userId: string): Promise<void> => {
    await api.delete(`/chat/groups/${threadId}/members/${userId}`);
  },

  promoteGroupAdmin: async (threadId: string, userId: string): Promise<void> => {
    await api.post(`/chat/groups/${threadId}/admins/${userId}`);
  },

  archiveGroup: async (threadId: string): Promise<void> => {
    await api.post(`/chat/groups/${threadId}/archive`);
  },

  groupReaders: async (threadId: string, messageId: string): Promise<ChatGroupReaders> => {
    const response = await api.get(`/chat/groups/${threadId}/messages/${messageId}/readers`);
    return response.data;
  },
};

export interface ChatGroupReader {
  userId: string;
  name: string;
  /** Horário da entidade; nulo = ainda não leu */
  readAt: string | null;
}

export interface ChatGroupReaders {
  read: ChatGroupReader[];
  pending: ChatGroupReader[];
}
